export const CODE_LABELS: Record<string, string> = {
  '*BAS': 'Boitier + Abonnement + SIM',
  '*AS':  'Abonnement + SIM',
  '*A':   'Abonnement',
  '*B':   'Boitier Gps',
  '*I':   'Installation',
  '*S':   'SIM seule',
  '*SR':  'SIM-Recharge',
};

export const getServiceLabel = (code: string): string =>
  CODE_LABELS[code] ?? code.replace('*', '');

export const getServiceDisplayName = (code?: string | null, nom?: string | null): string => {
  const trimmedNom = nom?.trim();
  return trimmedNom || getServiceLabel(code ?? '');
};

export type TypeClient = 'PARTICULIER' | 'AGENCE';
export type StatutCommande = 'DEVIS' | 'PAYEE' | 'FACTURE' | 'BON_COMMANDE';
export type StatutVehicule = 'INACTIF' | 'ACTIF';
export type ModePaiement = 'ESPECES' | 'VIREMENT' | 'CHEQUE';
export type CategorieDepense = 'ACHAT' | 'LOYER' | 'SALAIRE' | 'AUTRE';

export interface Client {
  id: number;
  nomRaisonSociale: string;
  type: TypeClient;
  telephone?: string;
  email?: string;
  note?: string;
  ice?: string;
  nomResponsable?: string;
  vehicules?: Vehicule[];
  commandes?: Commande[];
}

export interface ClientSummary {
  id: number;
  nomRaisonSociale: string;
  type: TypeClient;
  telephone?: string;
  email?: string;
  totalCommande: number;
  totalVerse: number;
  resteAPayer: number;
}

export interface PageResponse<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

export interface Vehicule {
  id: number;
  marque: string;
  modele: string;
  immatriculation: string;
  imei?: string;
  typeBoitier?: string;
  numTelSim?: string;
  statut?: StatutVehicule;
  dateExpirationAbonnement?: string;
  dateCreation?: string;
  client?: Client;
}

export interface BoitierStatus {
  imei: string;
  status: string;
  colorHex: string;
  idSuivre?: number;
}

export interface ProduitService {
  id: number;
  code: string;
  nom: string;
  prixUnitaire: number;
  detail?: string;
}

export interface LigneCommande {
  id: number;
  produitService: ProduitService;
  quantite: number;
  prixUnitaire: number;
  sousTotal: number;
  detail?: string;
}

export interface PaiementAvance {
  id: number;
  montant: number;
  date: string;
  modePaiement: ModePaiement;
}

export interface Commande {
  id: number;
  date: string;
  client: Client;
  vehicule?: Vehicule;
  vehiculesConcernes?: Vehicule[];
  lignesCommande: LigneCommande[];
  totalHT: number;
  tva: number;
  totalTTC: number;
  statut: StatutCommande;
  dureeAbonnementMois?: number;
  paiements: PaiementAvance[];
  dateEcheance?: string;
  numeroFacture?: string;
  numeroBonCommande?: string;
}

export type TypeStock = 'BOITIER' | 'SIM';
export type StatutStock = 'DISPONIBLE' | 'AFFECTE' | 'DEFECTUEUX';

export interface StockItem {
  id: number;
  type: TypeStock;
  reference?: string;
  modele?: string;
  statut: StatutStock;
  dateAchat?: string;
  notes?: string;
}

export interface StockBoitier {
  id: number;
  imei: string;
  modele?: string;
  statut: StatutStock;
  boitierClient?: boolean;
  dateAffectation?: string;
}

export interface StockCarteSim {
  id: number;
  numero: string;
  modele?: string;
  statut: StatutStock;
  dateAffectation?: string;
  dateDerniereRecharge?: string;
}

export type SimRechargeJobStatus =
  | 'PENDING'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'PARTIAL_FAILURE'
  | 'FAILED';

export type SimRechargeItemStatus =
  | 'PENDING'
  | 'IN_PROGRESS'
  | 'SUCCEEDED'
  | 'FAILED';

export interface SimRechargeItem {
  id: number;
  simId: number;
  numeroSim: string;
  status: SimRechargeItemStatus;
  attemptCount: number;
  workerId?: string;
  errorMessage?: string;
  claimedAt?: string;
  completedAt?: string;
}

export interface SimRechargeJob {
  id: number;
  status: SimRechargeJobStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
  totalCount: number;
  pendingCount: number;
  inProgressCount: number;
  succeededCount: number;
  failedCount: number;
  items: SimRechargeItem[];
}

export interface ModeleBoitier {
  id: number;
  nom: string;
}

export interface Depense {
  id: number;
  libelle: string;
  montant: number;
  date: string;
  categorie: CategorieDepense;
}

export interface DashboardData {
  chiffreAffairesTotal: number;
  totalAvances: number;
  totalResteARecouvrer: number;
  totalDepenses: number;
  evolutionMensuelle: { periode: string; montant: number }[];
}

export interface CommandeRequest {
  clientId: number;
  date: string;
  lignes: {
    produitServiceId: number;
    quantite: number;
    prixUnitaire?: number;
    detail?: string;
    vehiculeIds?: number[];
  }[];
  dureeAbonnementMois?: number;
  vehiculeId?: number;
  vehiculeIds?: number[];
  numeroFacture?: string;
  numeroBonCommande?: string;
  dateEcheance?: string;
  paiementImmediat?: boolean;
}

export type TypeAbonnement = 'PREMIER' | 'RENOUVELLEMENT';
export type TypeDocumentAbonnement = 'COMMANDE_EN_ATTENTE' | 'FACTURE' | 'BON_COMMANDE';
export type ModeAbonnementScreen = 'VEHICULE' | 'INSTALLATION' | 'RENOUVELLEMENT';

export interface ServiceLigneRequest {
  code: string;
  prixUnitaire?: number;
  quantite?: number;
  detail?: string;
}

export interface AbonnementCreationRequest {
  dureeAbonnementMois: number;
  prixCommande?: number;
  articleCode?: string;
  services?: ServiceLigneRequest[];
  numeroFacture?: string;
  dateEcheance?: string;
}

export interface AbonnementCreationResponse {
  vehiculeId: number;
  clientId: number;
  typeClient: TypeClient;
  typeAbonnement: TypeAbonnement;
  articleCode: string;
  dureeAbonnementMois: number;
  typeDocument: TypeDocumentAbonnement;
  commandeId: number;
  statutCommande: StatutCommande;
  message: string;
}

export interface AbonnementClientRequest {
  services: ServiceLigneRequest[];
  dureeAbonnementMois?: number;
  vehiculeIds: number[];
  numeroFacture?: string;
  dateEcheance?: string;
}

export interface AbonnementClientResponse {
  clientId: number;
  commandeId: number;
  statutCommande: StatutCommande;
  nbVehicules: number;
  message: string;
}

export type AuditAction =
  | 'CREATION'
  | 'MODIFICATION'
  | 'SUPPRESSION'
  | 'CONNEXION'
  | 'ECHEC_CONNEXION'
  | 'DECONNEXION'
  | 'EXPORT';

export interface AuditLogEntry {
  id: number;
  dateAction: string;
  username?: string | null;
  role?: string | null;
  action: AuditAction;
  entite?: string | null;
  entiteId?: string | null;
  description?: string | null;
  methode?: string | null;
  chemin?: string | null;
  statutHttp?: number | null;
  succes: boolean;
  adresseIp?: string | null;
  userAgent?: string | null;
  details?: string | null;
}

export interface AuditSummary {
  actions24h: number;
  actions7Jours: number;
  echecs7Jours: number;
  utilisateursActifs7Jours: number;
}
